import React from 'react';
import { connect } from 'react-redux';
import {
    Grid,
    Button,
    List,
    ListItem,
    ListItemText,
    IconButton,
    Checkbox,
    ListItemIcon,
    Tooltip,
    Box,
    SvgIcon
} from '@mui/material';
import { withStyles } from 'tss-react/mui';
import GetAppRoundedIcon from '@mui/icons-material/GetAppRounded';
import PlayCircleOutlineRounded from '@mui/icons-material/PlayCircleOutlineRounded';
import EditRoundedIcon from '@mui/icons-material/EditRounded';
import StarRoundedIcon from '@mui/icons-material/StarRounded';
import XIcon from '@mui/icons-material/X';
import FacebookIcon from '@mui/icons-material/Facebook';
import LinkIcon from '@mui/icons-material/LinkRounded';
import isEmpty from 'lodash.isempty';
import get from 'lodash.get';
import { Link } from 'react-router-dom';
import { trackEvent, AnalyticsConstants } from '../../../utilities/analyticsUtilities';
import PlanPageConstants from '../PlanPage/PlanPageConstants';
import { exportsActions } from './store';
import { CopyToClipboard } from 'react-copy-to-clipboard';
import { appOperations } from '../../../App/store';
import ColoredSnackbarConstants from '../../../components/misc/ColoredSnackbar/ColoredSnackbarConstants';
import { isRemoveWatermarkPossible, getPublicVideoLink } from './ExportsPageHelpers';
import { SocialPlatformNames } from './ExportsPageConstants';
import { mdiUpload } from '@mdi/js';
import YouTubeUploadDialog from '../../dialogs/YouTubeUploadDialog';
import ThirdPartyAuthDialog from '../../dialogs/ThirdPartyAuthDialog';

const styles = (theme) => ({
    button: {
        ".MuiBox-root": {
            width: "100%"
        },
        ".MuiButton-startIcon": {
            position: 'absolute',
            left: theme.spacing(2)
        }
    },
    actionListItem: {
        paddingTop: 0,
        paddingBottom: 0,
        paddingLeft: 0
    },
    actionListItemIcon: {
        minWidth: theme.spacing(4),
    },
    actionListItemSecondary: {
        right: 0
    },
    shareCheckbox: {
        padding: 0
    },
    actionLink: {
        textDecoration: 'none'
    }
});

class ExportActionsPanel extends React.Component {

    render() {
        const { classes, videoExports, selectedExport, accountDetails, videoInfo } = this.props;
        const removeWatermarkPossible = isRemoveWatermarkPossible(accountDetails, videoExports);

        const editVideoUrl = (videoInfo && videoInfo.id) ? `/create/${videoInfo.id}` : '#';
        const exportVideoUrl = (videoInfo && videoInfo.id) ? `/create/e/${videoInfo.id}` : '#';

        return (
            <>
                <Grid>
                    <Button
                        variant='outlined'
                        fullWidth={true} className={classes.button}
                        startIcon={<GetAppRoundedIcon />}
                        disabled={get(selectedExport, 'status') !== 'Completed'}
                        onClick={this.onDownloadVideoClick}
                        color='secondary'
                    >
                        <Box>Download Video</Box>
                    </Button>
                </ Grid>

                {removeWatermarkPossible &&
                    <Grid>
                        <Link to={'/plans'} className={classes.actionLink} tabIndex={-1}>
                            <Button
                                variant='outlined'
                                fullWidth={true}
                                className={classes.button}
                                startIcon={<StarRoundedIcon />}
                                onClick={this.onRemoveWatermarkClick}
                                color='secondary'
                            >
                                <Box>Remove Watermark</Box>
                            </Button>
                        </Link>
                    </ Grid>
                }

                <Grid />

                <Grid>
                    <Button
                        variant='outlined'
                        fullWidth={true}
                        className={classes.button}
                        startIcon={<SvgIcon> <path d={mdiUpload} /> </SvgIcon>}
                        disabled={get(selectedExport, 'status') !== 'Completed'}
                        onClick={() => this.onShareButtonClick(SocialPlatformNames.YOUTUBE)}
                        color='secondary'
                    >
                        <Box>Upload to YouTube</Box>
                    </Button>
                </ Grid>

                <Grid>
                    {this.renderShareItem()}
                </Grid>

                <Grid />

                <Grid>
                    <Link
                        to={editVideoUrl}
                        className={classes.actionLink}
                        tabIndex={-1}
                    >
                        <Button
                            variant='outlined'
                            color='secondary'
                            fullWidth={true}
                            className={classes.button}
                            startIcon={<EditRoundedIcon />}
                            onClick={this.onEditVideoClick}
                        >
                            <Box>Edit Video</Box>
                        </Button>
                    </Link>
                </ Grid>

                <Grid>
                    <Link
                        to={exportVideoUrl}
                        className={classes.actionLink}
                        tabIndex={-1}
                    >
                        <Button
                            variant='outlined'
                            color='secondary'
                            fullWidth={true}
                            className={classes.button}
                            startIcon={<PlayCircleOutlineRounded />}
                            onClick={this.onExportVideoClick}
                        >
                            <Box>Export Video</Box>
                        </Button>
                    </Link>
                </ Grid>
                <Grid />
            </>
        )
    }

    renderShareItem() {
        const { classes, selectedExport, videoInfo, youTubeUploadDialogOpen, googleAuthDialogOpen, googleAuthUrl } = this.props;

        return (
            <>
                <List disablePadding>
                    <ListItem className={classes.actionListItem}>
                        <ListItemIcon className={classes.actionListItemIcon}>
                            <Tooltip
                                title='When enabled, anyone with the link can view this video.'
                                placement='top'
                                arrow
                            >
                                <span>
                                    <Checkbox
                                        disableTouchRipple
                                        className={classes.shareCheckbox}
                                        color='default'
                                        inputProps={{ 'aria-labelledby': 'share-label-id' }}
                                        checked={(!isEmpty(videoInfo)) ? videoInfo.public : false}
                                        onChange={this.onVideoPublicToggle}
                                    />
                                </span>
                            </Tooltip>
                        </ListItemIcon>
                        <ListItemText id='share-label-id'>
                            Share
                        </ListItemText>
                        <Box sx={{ display: 'flex', marginRight: -2 }} className={classes.actionListItemSecondary}>
                            <Tooltip
                                title='Share to Facebook'
                                placement='top'
                                arrow
                            >
                                <span>
                                    <IconButton
                                        aria-label='Share to Facebook'
                                        onClick={() => this.onShareButtonClick(SocialPlatformNames.FACEBOOK)}
                                        size="large">
                                        <FacebookIcon />
                                    </IconButton>
                                </span>
                            </Tooltip>
                            <Tooltip
                                title='Share to X'
                                placement='top'
                                arrow
                            >
                                <span>
                                    <IconButton
                                        aria-label='Share to X'
                                        onClick={() => this.onShareButtonClick(SocialPlatformNames.TWITTER)}
                                        size="large">
                                        <XIcon/>
                                    </IconButton>
                                </span>
                            </Tooltip>
                            <Tooltip
                                title='Copy Link'
                                placement='top'
                                arrow
                            >

                                <span>
                                    <CopyToClipboard
                                        text={getPublicVideoLink(!isEmpty(selectedExport) ? selectedExport.exportId : '')}
                                        onCopy={this.onVideoLinkCopy}
                                    >
                                        <IconButton aria-label='Copy Link' size="large">
                                            <LinkIcon />
                                        </IconButton>
                                    </CopyToClipboard>
                                </span>
                            </Tooltip>
                        </Box>
                    </ListItem>
                </List>
                <YouTubeUploadDialog
                    open={youTubeUploadDialogOpen}
                    exportId={!isEmpty(selectedExport) ? selectedExport.exportId : ''}
                />
                <ThirdPartyAuthDialog
                    open={googleAuthDialogOpen}
                    googleAuthUrl={googleAuthUrl}
                    onClose={this.onThirdPartyAuthDialogClose}
                    title='Authorize Specterr'
                />
            </>
        );
    }

    onEditVideoClick = () => {
        trackEvent('Clicked CTA', {
            category: AnalyticsConstants.VIDEO_CATEGORY,
            color: 'green',
            destination: 'create page',
            location: 'exports page',
            text: 'EDIT VIDEO',
            type: 'button'
        });
    }

    onExportVideoClick = () => {
        trackEvent('Clicked CTA', {
            category: AnalyticsConstants.VIDEO_CATEGORY,
            color: 'green',
            destination: 'create page',
            location: 'exports page',
            text: 'EXPORT VIDEO',
            type: 'button'
        });
    }

    onRemoveWatermarkClick = () => {
        trackEvent('Clicked CTA', {
            category: AnalyticsConstants.ACQUISITION_CATEGORY,
            color: 'green',
            destination: PlanPageConstants.PLAN_PAGE_LOCATION,
            location: 'exports page',
            text: 'REMOVE WATERMARK',
            type: 'button'
        });
    }

    onDownloadVideoClick = () => {
        const { selectedExport, projectName, downloadVideoFile } = this.props;

        if (!isEmpty(selectedExport)) {
            downloadVideoFile(selectedExport, projectName, selectedExport.videoFileUrl);
        }
    }

    onVideoLinkCopy = () => {
        const { openSnack, videoInfo, changeVideoPublicStatus, selectedExport } = this.props;

        if (videoInfo.public) {
            openSnack('Copied!', ColoredSnackbarConstants.SNACKBAR_SUCCESS);
        } else {
            changeVideoPublicStatus(get(videoInfo, 'id'), true, (status) => {
                if (status) {
                    openSnack('Copied!', ColoredSnackbarConstants.SNACKBAR_SUCCESS);
                }
            });
        }

        trackEvent('Copied Export Link', {
            category: AnalyticsConstants.SOCIAL_CATEGORY,
            videoId: get(videoInfo, 'id'),
            exportId: selectedExport.exportId,
        });
    };

    onVideoPublicToggle = (event) => {
        const { videoInfo, changeVideoPublicStatus } = this.props;
        changeVideoPublicStatus(get(videoInfo, 'id'), event.target.checked);
    };

    onShareButtonClick = (socialNetwork) => {
        const { selectedExport, videoInfo, sharePublicVideo } = this.props;
        sharePublicVideo(socialNetwork, selectedExport.exportId, get(videoInfo, 'id'), videoInfo.public);
    };

    onThirdPartyAuthDialogClose = () => {
        const { setProperty } = this.props;
        setProperty('googleAuthDialogOpen', false);
    }
}

const mapStateToProps = (state) => {
    const {
        projectName,
        videoExports,
        selectedExport,
        videoInfo,
        youTubeUploadDialogOpen,
        googleAuthDialogOpen,
        googleAuthUrl
    } = state.exports;
    const {
        accountDetails
    } = state.app;

    return {
        projectName,
        videoExports,
        selectedExport,
        accountDetails,
        videoInfo,
        youTubeUploadDialogOpen,
        googleAuthDialogOpen,
        googleAuthUrl
    };
}

const mapDispatchToProps = (dispatch) => {
    const downloadVideoFile = (exportData, projectName, videoSource) => dispatch(exportsActions.downloadVideoFile(exportData, projectName, videoSource));
    const openSnack = (text, variant) => dispatch(appOperations.openSnack(text, variant));
    const changeVideoPublicStatus = (videoId, status, callback) => dispatch(exportsActions.changeVideoPublicStatus(videoId, status, callback));
    const sharePublicVideo = (socialNetwork, exportId, videoId, isVideoPublic) => dispatch(exportsActions.sharePublicVideo(socialNetwork, exportId, videoId, isVideoPublic));
    const setProperty = (key, value) => dispatch(exportsActions.setProperty(key, value));

    return {
        downloadVideoFile,
        openSnack,
        changeVideoPublicStatus,
        sharePublicVideo,
        setProperty
    };
};

export default connect(mapStateToProps, mapDispatchToProps)(withStyles(ExportActionsPanel, styles));